/** 页面水印 */
import request from './http-request';
import dayjs from 'dayjs';
import { getMyInfo } from '@/core/api/user';

interface WatermarkConfig {
  blackTransparency: number; // 黑底白字 - 字体不透明度
  whiteTransparency: number; // 白底黑字 - 字体不透明度
  isAddEnterpriseName: number; // 是否添加企业名称
  isAddUserName: number; // 是否添加用户名
  isAddShowName: number; // 是否添加显示名称
  isAddEmployee: number; // 是否添加手机号后4位
  isAddSystemTime: number; // 是否添加系统时间
  watermarkFieldName: string; // 配置名称
  watermarkFieldAliasName: string; // 配置code
}

type WatermarkTheme = 'black' | 'light';
interface WatermarkParams {
  code: string; // 配置code
  theme?: WatermarkTheme; // 主题
  config?: WatermarkConfig; // 水印配置
}

// api获取的水印配置
let watermarkConfig: WatermarkConfig[] | null = null;
// 前端定义的水印配置
const frontWatermarkConfig = {
  FRONT_CERTIFICATE_DETAIL: {
    watermarkFieldAliasName: 'FRONT_CERTIFICATE_DETAIL',
    watermarkFieldName: '证件详情',
    blackTransparency: 15,
    whiteTransparency: 8,
    isAddEnterpriseName: 0,
    isAddUserName: 0,
    isAddShowName: 1,
    isAddEmployee: 1,
    isAddSystemTime: 0,
  },
};

let userInfo: {
  groupId: number;
  groupName: string;
  mobilePhone?: string;
  showName: string;
  userName: string;
} | null = null;
const DOM_ID = 'page-watermark';

const jenerateWatermark = (config: WatermarkConfig, theme?: WatermarkTheme) => {
  const options = {
    font: '400 14px Microsoft Yahei',
    spacing: 160, //水印水平距离
    verticalSpacing: 120, //水印垂直距离
    rotate: -15,
    width: window.innerWidth,
    height: window.innerHeight,
  };

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;

  // 获取设备像素比
  const dpr = window.devicePixelRatio || 1;
  // 设置高分辨率尺寸
  canvas.width = options.width * dpr;
  canvas.height = options.height * dpr;
  // 设置 CSS 尺寸
  canvas.style.width = `${options.width}px`;
  canvas.style.height = `${options.height}px`;
  // 按比例缩放上下文
  ctx.scale(dpr, dpr);

  ctx.font = options.font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (theme === 'black') {
    ctx.fillStyle = '#fff';
    ctx.globalAlpha = config.blackTransparency / 100;
  } else {
    ctx.fillStyle = '#000';
    ctx.globalAlpha = config.whiteTransparency / 100;
  }

  // 组织水印文字内容
  const nameArr = [];
  if (config.isAddUserName) {
    nameArr.push(userInfo!.userName);
  }
  if (config.isAddShowName) {
    nameArr.push(userInfo!.showName);
  }
  if (config.isAddEmployee && userInfo!.mobilePhone) {
    nameArr.push(userInfo!.mobilePhone.slice(-4));
  }
  const content: string[] = [];
  if (config.isAddEnterpriseName) {
    // 企业名称
    content.push(userInfo!.groupName);
  }
  const nameStr = nameArr.join(' ').trim();
  if (nameStr) {
    // 用户信息
    content.push(nameStr);
  }
  if (config.isAddSystemTime) {
    // 系统时间
    let dateStr = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const timezoneOffset = -new Date().getTimezoneOffset() / 60;
    if (timezoneOffset !== 8) {
      // 东八区不显示 UTC
      dateStr += ` UTC${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`;
    }
    content.push(dateStr);
  }

  // 判断 content 是否为空, 否则 绘制水印 的 for循环 会死循环
  if (content.length === 0) {
    return canvas.toDataURL('image/png');
  }

  // 计算文字宽度
  let maxWidth = 0;
  content.forEach((item) => {
    const textWidth = ctx.measureText(item).width + options.spacing;
    maxWidth = Math.max(maxWidth, textWidth);
  });

  // 绘制水印
  ctx.rotate((Math.PI / 180) * options.rotate);
  for (let i = options.height * -0.5; i < options.height * 1.5; i += options.verticalSpacing) {
    for (let j = options.width * -0.5; j < options.width * 1.5; j += maxWidth) {
      // 填充文字，x 间距, y 间距
      const isEven = ((i - options.height * -0.5) / options.verticalSpacing) % 2;
      content.forEach((item, idx) => {
        if (isEven) {
          ctx.fillText(item, j + maxWidth / 2, i + idx * 20);
        } else {
          ctx.fillText(item, j, i + idx * 20);
        }
      });
    }
  }

  return canvas.toDataURL('image/png');
};

/**
 * 添加水印
 * @param params.code 配置code
 * @param params.theme 主题 'black' | 'light'
 */
export const addWatermark = (params: WatermarkParams) => {
  if (params.config) {
    addWatermarkDom(jenerateWatermark(params.config, params.theme));
  } else {
    getWatermark(params).then((base64) => {
      addWatermarkDom(base64);
    });
  }
};

/**
 * 移除水印
 */
export const removeWatermark = () => {
  const dom = document.getElementById(DOM_ID);
  if (dom) {
    dom.remove();
  }
  if (isAddedGlobal) {
    isAddedGlobal = false;
  }
};

/**
 * 获取水印图片base64
 * @param params.code 配置code
 * @param params.theme 主题 'black' | 'light'
 */
export const getWatermark = async (params: WatermarkParams) => {
  if (isAddedGlobal) {
    // 防止业务直接获取水印图片base64，导致水印图片重复添加
    return '';
  }

  await getUserInfo();
  if (!userInfo!.groupId) {
    console.log('🚀 ~ getWatermark: 没有企业id');
    return '';
  }

  let config: WatermarkConfig | undefined;
  if (params.code.startsWith('FRONT_')) {
    config = frontWatermarkConfig[params.code as keyof typeof frontWatermarkConfig];
  } else {
    await getWatermarkConfig(userInfo!.groupId);
    config = watermarkConfig!.find((item) => item.watermarkFieldAliasName === params.code);
  }

  if (!config) {
    console.log('🚀 ~ getWatermark: 该页面没有配置水印', params.code);
    return '';
  }
  return jenerateWatermark(config, params.theme);
};

/**
 * 添加水印dom
 * @param base64 水印图片base64
 */
const addWatermarkDom = (base64: string) => {
  if (isAddedGlobal || !base64) {
    // 已添加全局水印 或 没有水印图片
    return;
  }

  let dom = document.getElementById(DOM_ID);
  if (dom) {
    dom.style.backgroundImage = `url(${base64})`;
  } else {
    dom = document.createElement('div');
    dom.setAttribute('id', DOM_ID);
    dom.setAttribute(
      'style',
      ` position: fixed;
      top: 0;
      left: 0;
      z-index: 99999;
      width: 100vw;
      height: 100vh;
      pointer-events:none;
      background-repeat:repeat;
      background-size: ${window.innerWidth}px ${window.innerHeight}px;
      background-image:url('${base64}');
    `,
    );
    document.body.appendChild(dom);
  }
};

/**
 * 获取用户信息
 */
const getUserInfo = () => {
  return new Promise((resolve) => {
    if (userInfo) {
      resolve(userInfo);
      return;
    }
    getMyInfo()
      .then((myInfo) => {
        userInfo = (myInfo as any).data;
      })
      .catch((err) => {
        console.log('🚀 ~ getUserInfo: 获取用户信息失败', err);
        userInfo = {} as any;
      })
      .finally(() => {
        resolve(userInfo);
      });
  });
};

/**
 * 获取企业水印配置
 * @param groupId 企业id
 */
const getWatermarkConfig = (groupId: number) => {
  return new Promise((resolve) => {
    if (watermarkConfig) {
      resolve(watermarkConfig);
      return;
    }

    request({
      method: 'GET',
      url: '/ovopark-organize/enterpriseConfigureWatermark/getMergeWatermarkByGroupId',
      headers: {
        'Ovo-Authorization': window.ovopark.getToken(),
      },
      params: {
        groupId,
        setAllData: false,
      },
    })
      .then((res) => {
        watermarkConfig = res as WatermarkConfig[];
      })
      .catch((err) => {
        console.log('🚀 ~ getWatermarkConfig: 获取水印配置失败', err);
        watermarkConfig = [];
      })
      .finally(() => {
        resolve(watermarkConfig);
      });
  });
};

// 添加全局水印
let isAddedGlobal = false;
export const addGlobalWatermark = async (user: any) => {
  if (isAddedGlobal) {
    return;
  }

  if (user) {
    userInfo = user;
  } else {
    await getUserInfo();
  }
  if (!userInfo!.groupId) {
    console.log('🚀 ~ addGlobalWatermark: 没有企业id');
    return;
  }

  await getWatermarkConfig(userInfo!.groupId);
  const globalConfig = watermarkConfig!.find((item) => item.watermarkFieldAliasName === 'ALL');
  if (globalConfig) {
    addWatermark({ code: 'ALL', config: globalConfig });
    isAddedGlobal = true;
  }
};
